import { supabase } from "@/integrations/supabase/client";
import type { Tables, TablesInsert, TablesUpdate } from "@/integrations/supabase/types";
import { logger } from "@/utils/logger";

export type CancellationAlert = Tables<"cancellation_alerts">;
export type CancellationAlertInsert = TablesInsert<"cancellation_alerts">;
export type CancellationAlertUpdate = TablesUpdate<"cancellation_alerts">;

export interface CreateCancellationAlertParams {
  userId: string;
  bookingId: string;
  alertType: 'cancellation' | 'reschedule';
  eventType: 'shooting' | 'preparation' | 'editorial';
  bookingDate: Date;
  businessDaysNotice: number;
  feePercentage?: number;
  feeAmount?: number;
  attendeeName: string;
  attendeeEmail: string;
  plan: string;
  location?: string;
  notes?: string;
}

/**
 * Creates a new cancellation alert in the database
 */
export const createCancellationAlert = async (
  params: CreateCancellationAlertParams
): Promise<{ success: boolean; data?: CancellationAlert; error?: any }> => {
  try {
    logger.info('Creating cancellation alert', { 
      userId: params.userId, 
      bookingId: params.bookingId,
      alertType: params.alertType 
    });

    const { data, error } = await supabase
      .from('cancellation_alerts')
      .insert([{
        user_id: params.userId,
        booking_id: params.bookingId,
        alert_type: params.alertType,
        event_type: params.eventType,
        booking_date: params.bookingDate.toISOString(),
        business_days_notice: params.businessDaysNotice,
        fee_percentage: params.feePercentage,
        fee_amount: params.feeAmount,
        attendee_name: params.attendeeName,
        attendee_email: params.attendeeEmail,
        plan: params.plan,
        location: params.location,
        notes: params.notes,
        message: `${params.alertType === 'cancellation' ? 'Annulation' : 'Reprogrammation'} de ${params.eventType} - ${params.attendeeName}`,
      }])
      .select()
      .single();

    if (error) throw error;

    logger.info('Cancellation alert created successfully', { alertId: data.id });
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to create cancellation alert', { error, params });
    return { success: false, error };
  }
};

export interface GetCancellationAlertsFilters {
  isRead?: boolean;
  eventType?: 'shooting' | 'preparation' | 'editorial';
  limit?: number;
}

/**
 * Fetches cancellation alerts from the database
 */
export const getCancellationAlerts = async (
  filters?: GetCancellationAlertsFilters
): Promise<{ success: boolean; data?: CancellationAlert[]; error?: any }> => {
  try {
    logger.debug('Fetching cancellation alerts', { filters });

    let query = supabase
      .from('cancellation_alerts')
      .select('*')
      .order('created_at', { ascending: false });

    if (filters?.isRead !== undefined) {
      query = query.eq('is_read', filters.isRead);
    }

    if (filters?.eventType) {
      query = query.eq('event_type', filters.eventType);
    }

    if (filters?.limit) {
      query = query.limit(filters.limit);
    }

    const { data, error } = await query;

    if (error) throw error;

    logger.debug('Cancellation alerts fetched successfully', { count: data?.length });
    return { success: true, data: data || [] };
  } catch (error) {
    logger.error('Failed to fetch cancellation alerts', { error, filters });
    return { success: false, error };
  }
};

/**
 * Marks a cancellation alert as read
 */
export const markAlertAsRead = async (
  alertId: string
): Promise<{ success: boolean; data?: CancellationAlert; error?: any }> => {
  try {
    logger.info('Marking alert as read', { alertId });

    const { data, error } = await supabase
      .from('cancellation_alerts')
      .update({ is_read: true })
      .eq('id', alertId)
      .select()
      .single();

    if (error) throw error;

    logger.info('Alert marked as read successfully', { alertId });
    return { success: true, data };
  } catch (error) {
    logger.error('Failed to mark alert as read', { error, alertId });
    return { success: false, error };
  }
};

